import { DateTime } from "luxon";
const formatDate = (value: string, format?: any): string => {
  if (!DateTime.fromISO(value).isValid) {
    return value;
  }
  return DateTime.fromISO(value).toFormat(format || "dd-MM-yyyy");
};

const formatJsDate = (value: string, format?: any): string => {
  return DateTime.fromJSDate(new Date(value)).toFormat(format || "d-MMM");
};

const formatJsDateInDateMonthYear = (value: string): string => {
  return DateTime.fromJSDate(new Date(value)).toFormat("dd MMM yyyy");
};

function getMonthNameUtility(monthNumber: number): string {
  // Ensure the month number is valid (1-12)
  if (monthNumber < 1 || monthNumber > 12) {
    throw new Error("Invalid month number. Must be between 1 and 12.");
  }

  // Create a DateTime object and get the month name
  const monthName = DateTime.fromObject({ month: monthNumber }).toFormat("LLL");
  return monthName;
}
function formatToReadableDate(input: string | Date): string {
  if (!input) return "";

  let date: Date;

  // If already Date object
  if (input instanceof Date) {
    date = input;
  } else {
    const str = input.trim();

    // ISO format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      date = new Date(str);
    }

    // dd-mm-yyyy or dd-mm-yy
    else if (/^\d{2}-\d{2}-\d{2,4}$/.test(str)) {
      const [day, month, year] = str.split("-");
      const fullYear = year.length === 2 ? 2000 + Number(year) : Number(year);
      date = new Date(fullYear, Number(month) - 1, Number(day));
    }

    // dd/mm/yyyy or dd/mm/yy
    else if (/^\d{2}\/\d{2}\/\d{2,4}$/.test(str)) {
      const [day, month, year] = str.split("/");
      const fullYear = year.length === 2 ? 2000 + Number(year) : Number(year);
      date = new Date(fullYear, Number(month) - 1, Number(day));
    }

    // fallback (last attempt)
    else {
      date = new Date(str);
    }
  }

  // Invalid date check
  if (isNaN(date.getTime())) return "Invalid Date";

  // Format output
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
// eg: min year 18 max year 65 => minDate-1965 maxDate-2007 for input
function getCustomAddSubtractDate(
  dateString: string | "today",
  {
    years = 0,
    yearAction = "add",
    months = 0,
    monthAction = "add",
    days = 0,
    daysAction = "add",
  }: {
    years?: number;
    yearAction?: "add" | "sub";
    months?: number;
    monthAction?: "add" | "sub";
    days?: number;
    daysAction?: "add" | "sub";
  }
) {
  if (isNaN(years) || isNaN(months) || isNaN(days)) return;
  let d;
  if (dateString === "today") {
    d = new Date();
  } else {
    d = new Date(dateString);
  }

  // --- Year update ---
  if (years !== 0) {
    d.setFullYear(d.getFullYear() + (yearAction === "add" ? years : -years));
  }

  // --- Month update ---
  if (months !== 0) {
    d.setMonth(d.getMonth() + (monthAction === "add" ? months : -months));
  }

  // --- Days update ---
  if (days !== 0) {
    d.setDate(d.getDate() + (daysAction === "add" ? days : -days));
  }

  // Return YYYY-MM-DD (safest for input[type=date])
  return d.toISOString().split("T")[0];
}

type AgeUnit = "Y" | "M" | "D" | undefined;

const subtractYears = (date: Date, years: number) =>
  new Date(date.getFullYear() - years, date.getMonth(), date.getDate());

const subtractMonths = (date: Date, months: number) =>
  new Date(date.getFullYear(), date.getMonth() - months, date.getDate());

const subtractDays = (date: Date, days: number) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() - days + 1);

const format = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getMinMaxDateRangeFromAge = (age: number, unit: AgeUnit = "Y") => {
  const today = new Date();
  age = Number(age);
  if (!age || isNaN(age))
    return {
      modifiedMinDate: "",
      modifiedMaxDate: "",
    };

  let minDate: Date;
  let maxDate: Date;

  if (unit === "Y") {
    // Age in years
    maxDate = subtractYears(today, age);
    minDate = subtractYears(today, age + 1);
    minDate.setDate(minDate.getDate() + 1);
  } else if (unit === "M") {
    // Age in months
    maxDate = subtractMonths(today, age);
    minDate = subtractMonths(today, age + 1);
    minDate.setDate(minDate.getDate() + 1);
  } else {
    // Age in days
    maxDate = subtractDays(today, age);
    minDate = subtractDays(today, age + 1);
    minDate.setDate(minDate.getDate() + 1);
  }

  return {
    modifiedMinDate: format(minDate),
    modifiedMaxDate: format(maxDate),
  };
};

function calculateAgeFromDOB(dob?: string): number {
  if (!dob) return 0;
  const [yyyy, mm, dd] = dob.split("-");
  const birthDate = new Date(+yyyy, +mm - 1, +dd);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();

  const hasHadBirthdayThisYear =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() >= birthDate.getDate());

  if (!hasHadBirthdayThisYear) {
    age--;
  }
  return Math.max(age, 0);
}

const convertToInputDate = (dob: string) => {
  if (!dob) return "";
  const [dd, mm, yyyy] = dob.split("-");
  return `${yyyy}-${mm}-${dd}`;
};

function calculateDays(age: number, ageUnit: AgeUnit): number {
  if (ageUnit === "M") return age * 30;
  if (ageUnit === "Y") return age * 365;

  return age;
}

export {
  calculateDays,
  getCustomAddSubtractDate,
  formatDate,
  formatJsDate,
  formatJsDateInDateMonthYear,
  getMonthNameUtility,
  calculateAgeFromDOB,
  formatToReadableDate,
  getMinMaxDateRangeFromAge,
  convertToInputDate,
};
